<template><div><component-combinator component-name="DtTabGroup" /><h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-tab-group&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;1&quot; panel-id=&quot;2&quot; selected&gt;First&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;3&quot; panel-id=&quot;4&quot;&gt;Second&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;5&quot; panel-id=&quot;6&quot;&gt;Third&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<div class="d-w100p">
  <example-tabs />
</div>
</code-example>
<h3 id="borderless" tabindex="-1" class="d-docsite--header-3">Borderless <a class="header-anchor d-link d-docsite--link d-link" href="#borderless" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Remove the bottom border of any tablist.</p>
<code-example source-code='&lt;dt-tab-group borderless&gt;
  ...
&lt;/dt-tab-group&gt;'>
<div class="d-w100p">
  <example-tabs borderless />
  </div>
</code-example>
<h3 id="muted" tabindex="-1" class="d-docsite--header-3">Muted <a class="header-anchor d-link d-docsite--link d-link" href="#muted" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">All tabs render as muted buttons. The selected tab is distinguished with active styling.</p>
<code-example source-code='&lt;dt-tab-group kind=&quot;muted&quot;&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;1&quot; panel-id=&quot;2&quot; selected&gt;First&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;3&quot; panel-id=&quot;4&quot;&gt;Second&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;5&quot; panel-id=&quot;6&quot;&gt;Third&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<div class="d-w100p">
  <example-tabs kind="muted" />
</div>
</code-example>
<h3 id="outlined" tabindex="-1" class="d-docsite--header-3">Outlined <a class="header-anchor d-link d-docsite--link d-link" href="#outlined" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The selected tab renders with an outlined border instead of a filled style.</p>
<code-example source-code='&lt;dt-tab-group outlined&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;1&quot; panel-id=&quot;2&quot; selected&gt;First&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;3&quot; panel-id=&quot;4&quot;&gt;Second&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;5&quot; panel-id=&quot;6&quot;&gt;Third&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<div class="d-w100p">
  <example-tabs outlined />
</div>
</code-example>
<h3 id="muted-outlined" tabindex="-1" class="d-docsite--header-3">Muted Outlined <a class="header-anchor d-link d-docsite--link d-link" href="#muted-outlined" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Combines muted kind with outlined selected state.</p>
<code-example source-code='&lt;dt-tab-group kind=&quot;muted&quot; outlined&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;1&quot; panel-id=&quot;2&quot; selected&gt;First&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;3&quot; panel-id=&quot;4&quot;&gt;Second&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;5&quot; panel-id=&quot;6&quot;&gt;Third&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<div class="d-w100p">
  <example-tabs kind="muted" outlined />
</div>
</code-example>
<h3 id="disabled" tabindex="-1" class="d-docsite--header-3">Disabled <a class="header-anchor d-link d-docsite--link d-link" href="#disabled" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Add <code v-pre>disabled</code> to a specific tab.</p>
<code-example source-code='&lt;dt-tab-group&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;1&quot; panel-id=&quot;2&quot; selected&gt;First&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;3&quot; panel-id=&quot;4&quot;&gt;Second&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;5&quot; panel-id=&quot;6&quot;&gt;Third&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;7&quot; panel-id=&quot;8&quot; disabled&gt;Fourth&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<dt-tab-group>
  <template #tabs>
    <dt-tab id="1" panel-id="2" selected>First</dt-tab>
    <dt-tab id="3" panel-id="4">Second</dt-tab>
    <dt-tab id="5" panel-id="6">Third</dt-tab>
    <dt-tab id="7" panel-id="8" disabled>Fourth</dt-tab>
  </template>
</dt-tab-group>
</code-example>
<p class="d-docsite--paragraph">Add <code v-pre>disabled</code> to the tab group to disable all.</p>
<code-example source-code='&lt;dt-tab-group disabled&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;1&quot; panel-id=&quot;2&quot; selected&gt;First&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;3&quot; panel-id=&quot;4&quot;&gt;Second&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;5&quot; panel-id=&quot;6&quot;&gt;Third&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<example-tabs disabled />
</code-example>
<h3 id="inverted" tabindex="-1" class="d-docsite--header-3">Inverted <a class="header-anchor d-link d-docsite--link d-link" href="#inverted" aria-hidden="true">#</a></h3>
<dt-notice kind="warning" title="Deprecated" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
The <code v-pre>inverted</code> prop has been deprecated. Use the <RouteLink to="/components/mode-island.html#inverting" class="d-docsite--link d-link">v-dt-mode directive</RouteLink> instead.
</dt-notice>
<p class="d-docsite--paragraph">In place of the <code v-pre>inverted</code> prop, use the <RouteLink to="/components/mode-island.html#inverting" class="d-docsite--link d-link">v-dt-mode directive</RouteLink> on the component element.</p>
<code-example source-code='&lt;dt-tab-group v-dt-mode:invert&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;1&quot; panel-id=&quot;2&quot; selected&gt;First&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;3&quot; panel-id=&quot;4&quot;&gt;Second&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<div class="d-p-100 d-bgc-contrast d-w100p">
  <div v-dt-mode:invert class="d-p-200 d-bar-400">
    <example-tabs />
  </div>
</div>
</code-example>
<h2 id="spread" tabindex="-1" class="d-docsite--header-2">Spread <a class="header-anchor d-link d-docsite--link d-link" href="#spread" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Control how tabs distribute available horizontal space within the tab list. It only applies to horizontal tabs, and has no effect with <code v-pre>orientation=&quot;vertical&quot;</code>.</p>
<h3 id="grow" tabindex="-1" class="d-docsite--header-3">Grow <a class="header-anchor d-link d-docsite--link d-link" href="#grow" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Tabs expand proportionally to fill the container. Longer labels receive more space.</p>
<code-example source-code='&lt;dt-tab-group spread=&quot;grow&quot;&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;sg1&quot; panel-id=&quot;sg2&quot; selected&gt;Tab 1&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;sg3&quot; panel-id=&quot;sg4&quot;&gt;Tab the second&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;sg5&quot; panel-id=&quot;sg6&quot;&gt;Tab the third&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-tab-group spread="grow">
    <template #tabs>
      <dt-tab id="sg1" panel-id="sg2" selected>Tab 1</dt-tab>
      <dt-tab id="sg3" panel-id="sg4">Tab the second</dt-tab>
      <dt-tab id="sg5" panel-id="sg6">Tab the third</dt-tab>
    </template>
  </dt-tab-group>
</div>
</code-example>
<h3 id="equal" tabindex="-1" class="d-docsite--header-3">Equal <a class="header-anchor d-link d-docsite--link d-link" href="#equal" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">All tabs share the same width, regardless of label length.</p>
<code-example source-code='&lt;dt-tab-group spread=&quot;equal&quot;&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;se1&quot; panel-id=&quot;se2&quot; selected&gt;Tab 1&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;se3&quot; panel-id=&quot;se4&quot;&gt;Tab the second&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;se5&quot; panel-id=&quot;se6&quot;&gt;Tab the third&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<div data-demo-wrapper class="d-w-800">
  <dt-tab-group spread="equal">
    <template #tabs>
      <dt-tab id="se1" panel-id="se2" selected>Tab 1</dt-tab>
      <dt-tab id="se3" panel-id="se4">Tab the second</dt-tab>
      <dt-tab id="se5" panel-id="se6">Tab the third</dt-tab>
    </template>
  </dt-tab-group>
</div>
</code-example>
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<code-example source-code='&lt;dt-tab-group :size=&quot;100|200|300|400|500&quot;&gt;
  ...
&lt;/dt-tab-group&gt;'>
<dt-stack gap="200" class="d-w100p">
  <dt-tab-group :size="100">
    <template #tabs>
      <dt-tab id="1" panel-id="2" selected>
        First
      </dt-tab>
      <dt-tab id="3" panel-id="4">
        Second
      </dt-tab>
      <dt-tab id="5" panel-id="6">
        Third
      </dt-tab>
    </template>
  </dt-tab-group>
  <dt-tab-group :size="200">
    <template #tabs>
      <dt-tab id="1" panel-id="2" selected>
        First
      </dt-tab>
      <dt-tab id="3" panel-id="4">
        Second
      </dt-tab>
      <dt-tab id="5" panel-id="6">
        Third
      </dt-tab>
    </template>
  </dt-tab-group>
  <dt-tab-group>
    <template #tabs>
      <dt-tab id="1" panel-id="2" selected>
        First
      </dt-tab>
      <dt-tab id="3" panel-id="4">
        Second
      </dt-tab>
      <dt-tab id="5" panel-id="6">
        Third
      </dt-tab>
    </template>
  </dt-tab-group>
  <dt-tab-group :size="400">
    <template #tabs>
      <dt-tab id="1" panel-id="2" selected>
        First
      </dt-tab>
      <dt-tab id="3" panel-id="4">
        Second
      </dt-tab>
      <dt-tab id="5" panel-id="6">
        Third
      </dt-tab>
    </template>
  </dt-tab-group>
  <dt-tab-group :size="500">
    <template #tabs>
      <dt-tab id="1" panel-id="2" selected>
        First
      </dt-tab>
      <dt-tab id="3" panel-id="4">
        Second
      </dt-tab>
      <dt-tab id="5" panel-id="6">
        Third
      </dt-tab>
    </template>
  </dt-tab-group>
</dt-stack>
</code-example>
<h2 id="slots" tabindex="-1" class="d-docsite--header-2">Slots <a class="header-anchor d-link d-docsite--link d-link" href="#slots" aria-hidden="true">#</a></h2>
<h3 id="icon" tabindex="-1" class="d-docsite--header-3">Icon <a class="header-anchor d-link d-docsite--link d-link" href="#icon" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the <code v-pre>#startIcon</code> or <code v-pre>#endIcon</code> slot on <code v-pre>dt-tab</code> to add an icon. The slot provides <code v-pre>iconSize</code> to match the tab's size.</p>
<dt-notice kind="warning" title="Deprecated" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
The <code v-pre>#icon</code> slot has been deprecated. Use <code v-pre>#startIcon</code> or <code v-pre>#endIcon</code> instead.
</dt-notice>
<code-example source-code='&lt;dt-tab-group&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;1&quot; panel-id=&quot;2&quot; selected&gt;
      First
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon name=&quot;box-select&quot; :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
    &lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;3&quot; panel-id=&quot;4&quot;&gt;
      Second
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon name=&quot;box-select&quot; :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
      &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon name=&quot;box-select&quot; :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
    &lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;5&quot; panel-id=&quot;6&quot;&gt;
      Third
      &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon name=&quot;box-select&quot; :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
    &lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-tab-group>
    <template #tabs>
      <dt-tab id="1" panel-id="2" selected>
        First
        <template #startIcon="{ iconSize }">
          <dt-icon name="box-select" :size="iconSize" />
        </template>
      </dt-tab>
      <dt-tab id="3" panel-id="4">
        Second
        <template #startIcon="{ iconSize }">
          <dt-icon name="box-select" :size="iconSize" />
        </template>
        <template #endIcon="{ iconSize }">
          <dt-icon name="box-select" :size="iconSize" />
        </template>
      </dt-tab>
      <dt-tab id="5" panel-id="6">
        Third
        <template #endIcon="{ iconSize }">
          <dt-icon name="box-select" :size="iconSize" />
        </template>
      </dt-tab>
    </template>
  </dt-tab-group>
</div>
</code-example>
<h3 id="leading-trailing" tabindex="-1" class="d-docsite--header-3">Leading &amp; Trailing <a class="header-anchor d-link d-docsite--link d-link" href="#leading-trailing" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the <code v-pre>#leading</code> and <code v-pre>#trailing</code> slots on <code v-pre>dt-tab</code> to render content like badges or count indicators alongside tab labels. Use <code v-pre>leading-class</code> and <code v-pre>trailing-class</code> to adjust padding.</p>
<code-example source-code='&lt;dt-tab-group&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;lt1&quot; panel-id=&quot;lt2&quot; selected trailing-class=&quot;d-pie-100&quot;&gt;
      Inbox
      &lt;template #trailing&gt;
        &lt;dt-badge kind=&quot;count&quot; type=&quot;bulletin&quot; text=&quot;9&quot; /&gt;
      &lt;/template&gt;
    &lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;lt3&quot; panel-id=&quot;lt4&quot; trailing-class=&quot;d-pie-100&quot;&gt;
      Archive
      &lt;template #trailing&gt;
        &lt;dt-badge kind=&quot;count&quot; text=&quot;99+&quot; /&gt;
      &lt;/template&gt;
    &lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;lt5&quot; panel-id=&quot;lt6&quot;&gt;
      Drafts
    &lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-tab-group>
    <template #tabs>
      <dt-tab id="lt1" panel-id="lt2" selected trailing-class="d-pie-100">
        Inbox
        <template #trailing>
          <dt-badge kind="count" type="bulletin" text="9" />
        </template>
      </dt-tab>
      <dt-tab id="lt3" panel-id="lt4" trailing-class="d-pie-100">
        Archive
        <template #trailing>
          <dt-badge kind="count" text="99+" />
        </template>
      </dt-tab>
      <dt-tab id="lt5" panel-id="lt6">
        Drafts
      </dt-tab>
    </template>
  </dt-tab-group>
</div>
</code-example>
<h2 id="orientation" tabindex="-1" class="d-docsite--header-2">Orientation <a class="header-anchor d-link d-docsite--link d-link" href="#orientation" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Set <code v-pre>orientation=&quot;vertical&quot;</code> to stack tabs vertically alongside the panel.</p>
<code-example source-code='&lt;dt-tab-group orientation=&quot;vertical&quot;&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;1&quot; panel-id=&quot;2&quot; selected&gt;First&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;3&quot; panel-id=&quot;4&quot;&gt;Second&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;5&quot; panel-id=&quot;6&quot;&gt;Third&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<example-tabs orientation="vertical" />
</code-example>
<h2 id="advanced-usages" tabindex="-1" class="d-docsite--header-2">Advanced Usages <a class="header-anchor d-link d-docsite--link d-link" href="#advanced-usages" aria-hidden="true">#</a></h2>
<h3 id="automatic-mode" tabindex="-1" class="d-docsite--header-3">Automatic Mode <a class="header-anchor d-link d-docsite--link d-link" href="#automatic-mode" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">By default, tabs use manual activation — the user must press <code v-pre>Enter</code> or <code v-pre>Space</code> after focusing a tab to select it. Set <code v-pre>activation-mode=&quot;auto&quot;</code> to select tabs immediately on focus via arrow keys, following the <a class="d-link" href="https://www.w3.org/WAI/ARIA/apg/patterns/tabs/" target="_blank">WAI-ARIA Tabs pattern</a>.</p>
<code-example source-code='&lt;dt-tab-group activation-mode=&quot;auto&quot;&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;1&quot; panel-id=&quot;2&quot; selected&gt;First&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;3&quot; panel-id=&quot;4&quot;&gt;Second&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;5&quot; panel-id=&quot;6&quot;&gt;Third&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;'>
<example-tabs activation-mode="auto" />
</code-example>
<h3 id="validation-before-changing-tabs" tabindex="-1" class="d-docsite--header-3">Validation Before Changing Tabs <a class="header-anchor d-link d-docsite--link d-link" href="#validation-before-changing-tabs" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">If you need to do some validation before changing tabs, you can use the <code v-pre>before-change</code> event. If the event handler is prevented, the tab change will be cancelled.</p>
<code-example source-code='&lt;dt-tab-group @before-change=&quot;confirmBeforeLeave&quot;&gt;
  &lt;template #tabs&gt;
    &lt;dt-tab id=&quot;1&quot; panel-id=&quot;2&quot; selected&gt;First&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;3&quot; panel-id=&quot;4&quot;&gt;Second&lt;/dt-tab&gt;
    &lt;dt-tab id=&quot;5&quot; panel-id=&quot;6&quot;&gt;Third&lt;/dt-tab&gt;
  &lt;/template&gt;
&lt;/dt-tab-group&gt;
&lt;script setup&gt;
  function confirmBeforeLeave (event) {
    const confirmed = confirm(&quot;Are you sure to change tab?&quot;);
    if (!confirmed) {
      event.preventDefault();
    }
  }
&lt;/script&gt;'>
<example-tabs validate />
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<h3 id="tab-group" tabindex="-1" class="d-docsite--header-3">Tab Group <a class="header-anchor d-link d-docsite--link d-link" href="#tab-group" aria-hidden="true">#</a></h3>
<component-vue-api component-name="tabgroup" :also-import="['tab', 'tabpanel']" /><h3 id="tab-panel" tabindex="-1" class="d-docsite--header-3">Tab Panel <a class="header-anchor d-link d-docsite--link d-link" href="#tab-panel" aria-hidden="true">#</a></h3>
<component-vue-api component-name="tabpanel" :show-import="false" /><h3 id="tab" tabindex="-1" class="d-docsite--header-3">Tab <a class="header-anchor d-link d-docsite--link d-link" href="#tab" aria-hidden="true">#</a></h3>
<component-vue-api component-name="tab" :show-import="false" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="tabs" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">To create accessible tabs, be sure to implement the <a class="d-link" href="https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-2/tabs.html" target="_blank">proper keyboard navigation</a> and utilize the following ARIA roles to properly declare element roles, content relationships, and current status:</p>
<component-accessible-table component-name="tabs" /></div></template>


<script setup>
  import ExampleTabs from "@exampleComponents/ExampleTabs.vue";
</script>